
<template>
  <!-- helpbar -->
  <HelpBar />
  
  <v-card class="project-card" elevation="2" rounded="lg">
    <!-- Header -->
    <v-toolbar flat class="project-toolbar">
      <div class="project-toolbar-content">
        <div class="project-title-block">
          <div class="project-title-icon">
            <v-icon color="primary" size="28">
              mdi-chart-areaspline
            </v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Einsparpotential
            </div>

            <div class="text-body-2 text-medium-emphasis">
              Einsparungen, Investitionen und energetische Auswirkungen vergleichen.
            </div>
          </div>
        </div>

        <div class="project-toolbar-actions">
          <ProjectPageNav />

          <!-- Percentage toggle (restored) -->
          <v-btn
            @click="togglePercentageView"
            color="primary"
            variant="tonal"
            class="toolbar-button"
            title="Zwischen absoluten und relativen Werten wechseln"
          >
            <v-icon start>
              {{ showPercentages ? 'mdi-numeric' : 'mdi-percent' }}
            </v-icon>

            {{ showPercentages ? 'Absolute Werte' : 'Relative Werte' }}
          </v-btn>

          <!-- Help button -->
          <v-tooltip
            text="Hilfe anzeigen"
            location="bottom"
            color="surface" 
            content-class="tooltip"
            :disabled="store.sidebarShown"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                variant="flat"
                class="toolbar-button"
                @click="
                  store.sidebarShown
                    ? store.hideSidebar()
                    : store.showSidebar(ProjectCalcHelp)
                "
              >
                <v-icon start>
                  {{
                    store.sidebarShown
                      ? 'mdi-close'
                      : 'mdi-help-circle-outline'
                  }}
                </v-icon>

                Hilfe
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-toolbar>

    <v-divider />

    <!-- Errors -->
    <v-card-text
      v-if="errorResponse && errorResponse.length"
      class="pb-0"
    >
      <v-alert
        type="error"
        variant="tonal"
        border="start"
        class="calculation-message"
      >
        <template #title>
          Berechnung nicht vollständig
        </template>

        <div class="message-description">
          Bei der Berechnung sind folgende Fehler aufgetreten:
        </div>

        <ul class="message-list">
          <li
            v-for="(error, idx) in errorResponse"
            :key="'err-' + idx"
          >
            <span>{{ error }}</span>

            <v-tooltip
              text="Die fehlende Größe bezieht sich auf die Nutzenergie. Bitte geben Sie die erforderlichen Werte für alle verwendeten Endenergien in den Projektdetails an."
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="18"
                  color="error"
                  class="ml-2"
                >
                  mdi-information-outline
                </v-icon>
              </template>
            </v-tooltip>
          </li>
        </ul>
      </v-alert>
    </v-card-text>

    <!-- Warnings -->
    <v-card-text
      v-if="warningResponse && warningResponse.length"
      class="pb-0"
    >
      <v-alert
        type="warning"
        variant="tonal"
        border="start"
        class="calculation-message"
      >
        <template #title>
          Hinweise zur Berechnung
        </template>

        <div class="message-description">
          Die Berechnung wurde durchgeführt, enthält jedoch folgende Hinweise:
        </div>

        <ul class="message-list">
          <li
            v-for="(warning, idx) in warningResponse"
            :key="'warn-' + idx"
          >
            <span>{{ warning }}</span>

            <v-tooltip
              text="Bitte prüfen und ergänzen Sie die fehlende Größe in den Projektdetails."
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="18"
                  color="warning"
                  class="ml-2"
                >
                  mdi-information-outline
                </v-icon>
              </template>
            </v-tooltip>
          </li>
        </ul>
      </v-alert>
    </v-card-text>

    <!-- Results table -->
    <div v-if="actionResults && actionResults.length">
      <v-card-text>
        <v-sheet class="analysis-summary" elevation="0">
          <div class="analysis-summary-left">
            <v-chip color="primary" variant="tonal">
              <v-icon start>mdi-format-list-checks</v-icon>
              {{ actionResults.length }}
              Maßnahme{{ actionResults.length === 1 ? '' : 'n' }}
            </v-chip>

            <v-chip
              v-if="actionGroupResults.length"
              color="secondary"
              variant="tonal"
            >
              <v-icon start>mdi-folder-outline</v-icon>
              {{ actionGroupResults.length }}
              Gruppe{{ actionGroupResults.length === 1 ? '' : 'n' }}
            </v-chip>

            <v-chip color="primary" variant="outlined">
              <v-icon start>
                {{ showPercentages ? 'mdi-percent' : 'mdi-numeric' }}
              </v-icon>

              {{
                showPercentages
                  ? 'Relative Darstellung'
                  : 'Absolute Darstellung'
              }}
            </v-chip>
          </div>

          <div class="analysis-summary-right">
            <span class="text-body-2 text-medium-emphasis">
              Gruppen und Maßnahmen können ein- und ausgeklappt werden.
            </span>
          </div>
        </v-sheet>

        <div class="analysis-table-wrapper">
          <v-table class="project-table" density="comfortable" hover>
            <thead>
              <tr>
                <th colspan="2"></th>

                <!-- Einsparung & Leistungszahlen group header -->
                <th
                  class="text-center alt"
                  :colspan="showFeedInColumn ? 4 : 3"
                >
                  Einsparung und Leistungszahlen
                </th>

                <th class="text-center alt">Investition</th>

                <!-- Endenergien -->
                <th
                  v-if="energiesEElength > 0"
                  class="text-center alt"
                  :colspan="energiesEElength"
                >
                  Endenergien
                </th>

                <!-- Nutzenergien -->
                <th
                  v-if="energiesNElength > 0"
                  class="text-center alt"
                  :colspan="energiesNElength"
                >
                  Nutzenergien
                </th>

                <!-- Eigenverbrauch Erneuerbare -->
                <th
                  v-if="energiesRElength > 0"
                  class="text-center alt"
                  :colspan="energiesRElength"
                >
                  Eigenverbrauch Erneuerbare Energie
                </th>

                <!-- Einspeisung Erneuerbare -->
                <th
                  v-if="energiesENlength > 0"
                  class="text-center alt"
                  :colspan="energiesENlength"
                >
                  Einspeisung Erneuerbare Energie
                </th>

                <th class="text-center sticky-report-column"></th>
              </tr>

              <tr>
                <th>Name</th>
                <th>Maßnahme</th>

                <th class="text-right">
                  Energiekosten {{ showPercentages ? '%' : '[€/a]' }}
                </th>

                <th class="text-right" v-if="showFeedInColumn">
                  Einspeisung {{ showPercentages ? '%' : '[€/a]' }}
                </th>

                <th class="text-right">
                  <!-- Als Ersparnis ausgewiesen wie alle anderen Spalten:
                       `deltaOperationalCost` ist plan minus Bestand und damit
                       negativ, wenn die Maßnahme Wartung spart. -->
                  Wartungskosten {{ showPercentages ? '%' : '[€/a]' }}
                </th>

                <th class="text-right">
                  CO₂ {{ showPercentages ? '%' : '[kg/a]' }}
                </th>

                <th class="text-right">Investition [€]</th>

                <th
                  class="text-right"
                  v-for="eid in energies"
                  :key="'energy-col-' + eid"
                >
                  {{ getEnergyName(eid) }} {{ showPercentages ? '%' : '[kWh]' }}
                </th>

                <th class="text-center sticky-report-column">
                  Kurzbericht
                </th>

              </tr>
            </thead>

            <tbody>
              <!-- Grouped results -->
              <template v-for="ag in actionGroupResults" :key="'group-' + ag.id">
                <tr>
                  <td
                    colspan="2"
                    class="grouptitle"
                    :class="{ opened: !toggledActionGroup?.includes(ag.id) }"
                    @click="toggleGroup(ag.id)"
                  >
                    <v-icon small>
                      {{ !toggledActionGroup?.includes(ag.id) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                    {{ getGroupName(ag.id) }}
                  </td>

                  <!-- Energiekosten -->
                  <td class="text-right group" @click="toggleGroup(ag.id)">
                    <template v-if="showPercentages">
                      {{ fmtPercent(ag.total?.energyCostSavingsPercentage ?? 0) }}
                    </template>
                    <template v-else>
                      {{ fmt(ag.total?.energyCostSavings) }}
                    </template>
                  </td>

                  <!-- Einspeisung -->
                  <td
                    v-if="showFeedInColumn"
                    class="text-right group"
                    @click="toggleGroup(ag.id)"
                  >
                    <template v-if="showPercentages">
                      {{ fmtPercent(ag.total?.feedInProfitsPercentage ?? 0) }}
                    </template>
                    <template v-else>
                      {{ fmt(ag.total?.feedInProfits) }}
                    </template>
                  </td>

                  <!-- Wartungskosten -->
                  <td class="text-right group" @click="toggleGroup(ag.id)">
                    <template v-if="showPercentages">
                      {{ fmtPercent(ag.total?.deltaOperationalCostPercentage ?? 0) }}
                    </template>
                    <template v-else>
                      {{ fmt(-(ag.total?.deltaOperationalCost ?? 0)) }}
                    </template>
                  </td>

                  <!-- CO₂ (kg/a) -->
                  <td class="text-right group" @click="toggleGroup(ag.id)">
                    <template v-if="showPercentages">
                      {{ fmtPercent(ag.total?.co2SavingsPercentage ?? 0) }}
                    </template>
                    <template v-else>
                      {{ fmt((ag.total?.co2Savings || 0) / 1000) }}
                    </template>
                  </td>

                  <!-- Invest -->
                  <td class="text-right group" @click="toggleGroup(ag.id)">
                    {{ fmt(ag.total?.invest) }}
                  </td>

                  <!-- Energies (all categories in one loop; the header segmentation is visual) -->
                  <td
                    v-for="eid in energies"
                    :key="'group-energy-' + ag.id + '-' + eid"
                    class="text-right group"
                    @click="toggleGroup(ag.id)"
                  >
                    <template v-if="showPercentages">
                      <template v-if="ag.total?.energySavingsPercentage && ag.total.energySavingsPercentage[eid] !== undefined">
                        {{ fmtPercent(ag.total.energySavingsPercentage[eid]) }}
                      </template>
                      <template v-else>-</template>
                    </template>
                    <template v-else>
                      {{ fmt(ag.total?.energySavings?.[eid]) }}
                    </template>
                  </td>

                  <td class="group text-center sticky-report-column">
                    <div class="group-actions">
                      <v-btn
                        :href="docxDownloadHrefGroup(ag.id)"
                        color="primary"
                        variant="text"
                        size="small"
                        density="comfortable"
                        :ripple="false"
                        icon
                        title="Kurzbericht dieser Gruppe als Word Export"
                        @click.stop
                      >
                        <v-icon>mdi-file-word</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <!-- Actions within a group -->
                <template v-for="action in ag.actions" :key="'action-' + action.id">
                  <ProjectCalcActionRow
                    v-show="!toggledActionGroup?.includes(ag.id)"
                    class="action"
                    indent
                    :action="action"
                    :energies="energies"
                    :methodTexts="methodTexts"
                    :toggledAction="toggledAction"
                    :showPercentages="showPercentages"
                    :showFeedInColumn="showFeedInColumn"
                    @toggleAction="handleToggleAction"
                  />

                  <ProjectCalcGeneratorsRow
                    v-show="!toggledAction?.includes(action.id)"
                    v-for="gen in action.linkedGenerators || []"
                    :key="'gen-' + gen.id"
                    :gen="gen"
                    :energies="energies"
                    :methodTexts="methodTexts" 
                    :showPercentages="showPercentages"
                    :showFeedInColumn="showFeedInColumn"
                  />
                </template>
              </template>

              <!-- Ungrouped actions (if any) -->
              <template v-for="action in actionResults" :key="'action-ungrouped-' + action.id">
                <ProjectCalcActionRow
                  class="action"
                  :action="action"
                  :energies="energies"
                  :methodTexts="methodTexts"
                  :toggledAction="toggledAction"
                  :showPercentages="showPercentages"
                  :showFeedInColumn="showFeedInColumn"
                  @toggleAction="handleToggleAction"
                />
                <ProjectCalcGeneratorsRow
                  v-show="!toggledAction?.includes(action.id)"
                  v-for="gen in action.linkedGenerators || []"
                  :key="'gen-ungrouped-' + gen.id"
                  :gen="gen"
                  :energies="energies"
                  :methodTexts="methodTexts"
                  :showPercentages="showPercentages"
                  :showFeedInColumn="showFeedInColumn"
                />
              </template>
            </tbody>
            <tfoot>
              <!-- Totals -->
              <tr class="total-row">
                <td>
                  <div class="total-label">
                    <v-icon size="20" color="primary">
                      mdi-sigma
                    </v-icon>

                    <span>Summe</span>
                  </div>
                </td>
                <td></td>

                <!-- Energiekosten -->
                <td class="text-right">
                  <template v-if="showPercentages">
                    {{ fmtPercent(totalResult?.energyCostSavingsPercentage ?? 0) }}
                  </template>
                  <template v-else>
                    {{ fmt(totalResult?.energyCostSavings) }}
                  </template>
                </td>

                <!-- Einspeisung -->
                <td class="text-right" v-if="showFeedInColumn">
                  <template v-if="showPercentages">
                    {{ fmtPercent(totalResult?.feedInProfitsPercentage ?? 0) }}
                  </template>
                  <template v-else>
                    {{ fmt(totalResult?.feedInProfits) }}
                  </template>
                </td>

                <!-- Wartungskosten -->
                <td class="text-right">
                  <template v-if="showPercentages">
                    {{ fmtPercent(totalResult?.deltaOperationalCostPercentage ?? 0) }}
                  </template>
                  <template v-else>
                    {{ fmt(-(totalResult?.deltaOperationalCost ?? 0)) }}
                  </template>
                </td>

                <!-- CO₂ -->
                <td class="text-right">
                  <template v-if="showPercentages">
                    {{ fmtPercent(totalResult?.co2SavingsPercentage ?? 0) }}
                  </template>
                  <template v-else>
                    {{ fmt((totalResult?.co2Savings || 0) / 1000) }}
                  </template>
                </td>

                <!-- Investition -->
                <td class="text-right">{{ fmt(totalResult?.invest) }}</td>

                <!-- Energies -->
                <td
                  class="text-right"
                  v-for="eid in energies"
                  :key="'total-energy-' + eid"
                >
                  <template v-if="showPercentages">
                    <template v-if="totalResult?.energySavingsPercentage && totalResult.energySavingsPercentage[eid] !== undefined">
                      {{ fmtPercent(totalResult.energySavingsPercentage[eid]) }}
                    </template>
                    <template v-else>-</template>
                  </template>
                  <template v-else>
                    {{ fmt(totalResult?.energySavings?.[eid]) }}
                  </template>
                </td>

                <td></td>
              </tr>
            </tfoot>
          </v-table>
        </div>

        <!-- Bottom bar: Legend (left) + Downloads (right) -->
        <v-row no-gutters class="mt-4">
          <!-- Legends -->
          <v-col cols="auto" v-if="Object.keys(showWarmColors).length > 0" class="mr-2">
            <v-card class="legend-card" variant="outlined">

              <div class="legend-title">
                <v-icon size="20" color="primary">
                  mdi-fire
                </v-icon>

                <span>Verknüpfte Wärmeerzeuger</span>
              </div>

              <div v-for="(color, id) in showWarmColors" :key="'legend-' + id" class="mb-2">
                <span class="d-flex align-center">
                  <span
                    class="generator-color-dot"
                    :style="{ backgroundColor: color }"
                    :title="String(id)"
                  ></span>
                  <!-- <span>
                    <button
                      :style="{
                        backgroundColor: color,
                        border: 'none',
                        borderRadius: '50%',
                        width: '10px',
                        height: '10px',
                        cursor: 'default'
                      }"
                      :title="String(id)"
                    ></button>
                  </span> -->
                  <span class="ml-2">{{ getGenerator(Number(id)) }}</span>
                </span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="auto" v-if="Object.keys(showColdColors).length > 0" class="ml-2">
            <v-card class="legend-card" variant="outlined">

              <div class="legend-title">
                <v-icon size="20" color="primary">
                  mdi-snowflake
                </v-icon>

                <span>Verknüpfte Kälteerzeuger</span>
              </div>

              <div v-for="(color, id) in showColdColors" :key="'legend-' + id" class="mb-2">
                <span class="d-flex align-center">
                  <span
                    class="generator-color-dot"
                    :style="{ backgroundColor: color }"
                    :title="String(id)"
                  ></span>
                  <!-- <span>
                    <button
                      :style="{
                        backgroundColor: color,
                        border: 'none',
                        borderRadius: '50%',
                        width: '10px',
                        height: '10px',
                        cursor: 'default'
                      }"
                      :title="String(id)"
                    ></button>
                  </span> -->
                  <span class="ml-2">{{ getGenerator(Number(id)) }}</span>
                </span>
              </div>
            </v-card>
          </v-col>

          <v-spacer />

          <!-- Downloads -->
          <v-col cols="12" lg="auto">
            <v-card class="export-card" variant="outlined">
              <div class="export-card-content">
                <div class="export-title">
                  <v-icon color="primary">mdi-download-outline</v-icon>

                  <div>
                    <div class="font-weight-bold">
                      Analyse exportieren
                    </div>

                    <div class="text-caption text-medium-emphasis">
                      Berichte und Tabellen herunterladen
                    </div>
                  </div>
                </div>

                <div class="export-actions">
                  <v-btn
                    :href="docxDownloadHref"
                    color="primary"
                    title="Bericht aller Maßnahmen als Word Export"
                  >
                    <v-icon start>mdi-file-word-outline</v-icon>
                    Word-Bericht
                  </v-btn>

                  <v-btn
                    :href="zipDownloadHref"
                    color="primary"
                    title="Alle Kurzberichte der Maßnahmen als Zip Export"
                  >
                    <v-icon start>mdi-folder-zip-outline</v-icon>
                    Kurzberichte
                  </v-btn>

                  <v-btn
                    :href="excelDownloadHref"
                    color="primary"
                    title="Analyse Tabelle als Excel Export"
                  >
                    <v-icon start>mdi-file-excel-outline</v-icon>
                    Excel
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeMount } from "vue";
import ProjectPageNav from '@/components/ProjectPageNav.vue';
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

import api from "@/plugins/axios";
import { useProjectStore } from "@/stores/projectStore";

import HelpBar from "@/components/HelpBar.vue";
import ProjectCalcActionRow from "@/components/projectcalc/ProjectCalcActionRow.vue";
import ProjectCalcGeneratorsRow from "@/components/projectcalc/ProjectCalcGeneratorsRow.vue";
import ProjectCalcHelp from "@/components/help/ProjectCalcHelp.vue";

/* ---------------------------------------
 * Store & Route
 * ------------------------------------- */
const store = useProjectStore();
const route = useRoute();
const projectId = computed(() => Number(route.params.projectId));

/* ---------------------------------------
 * Local state
 * ------------------------------------- */
const toggledActionGroup = ref([]); // contains COLLAPSED group IDs (like Vue 2)
const toggledAction = ref([]);      // hidden actions for generators (ids)
const methodTexts = ref(null);
const calcResponse = ref(null);
const errorResponse = ref(null);
const warningResponse = ref(null);

// fuels map: id -> name
const fuels = ref({});

// restored: percentage mode + legend colors
const showPercentages = ref(false);
const showWarmColors = ref({}); // { generatorId: color }
const showColdColors = ref({})

// stores all the selected gens linked to actions 
const allGens = ref([]);


/* ---------------------------------------
 * Helpers
 * ------------------------------------- */
const numberFmt = new Intl.NumberFormat("de-DE");
const percentFmt = new Intl.NumberFormat("de-DE", {
  style: "percent",
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const fmt = (value) => {
  const num = Number(value ?? 0);
  return numberFmt.format(isFinite(num) ? num : 0);
};

// Modern percentage: expect fraction (0.1234 -> "12,3 %")
const fmtPercent = (fraction) => {
  const f = Number(fraction ?? 0);
  return percentFmt.format(isFinite(f) ? f : 0);
};

/*
 * Die selbst benannten Wärmequellen des Projekts (Energieträger 24–26).
 * `/projects/fuels/` führt nur den Katalognamen — „Biogas" gilt für einen
 * Standort und nicht für den Katalog.
 */
const customEnergyNames = computed(() => {
  const names = {};
  for (const entry of store.currentProject?.energy_data || []) {
    if (entry.custom_name) names[Number(entry.fuel)] = entry.custom_name;
  }
  return names;
});

const getEnergyName = (eid) =>
  customEnergyNames.value[Number(eid)] ?? fuels.value?.[eid] ?? "Energieart n/a";

const groupsMap = computed(() => {
  const map = {};
  for (const g of store.currentProjectActionGroups || []) {
    if (g?.id != null) map[g.id] = g;
  }
  return map;
});

const getGroupName = (groupId) => groupsMap.value[groupId]?.name ?? "n/a";

/* ---------------------------------------
 * Derived data from calcResponse
 * ------------------------------------- */
const actionGroupResults = computed(() => {
  return calcResponse.value?.result?.actionGroups || [];
 
});

const actionResults = computed(() => {
  return calcResponse.value?.result?.actions || [];
});

const totalResult = computed(() => calcResponse.value?.result?.total);
const resultId = computed(() => calcResponse.value?.id || null);

/* Downloads (keep your current routes) */
const excelDownloadHref = computed(() =>
  resultId.value ? `/api/v1/results/${resultId.value}/excel/` : undefined
);
const docxDownloadHref = computed(() =>
  projectId.value ? `/api/v1/projects/${projectId.value}/docx/` : undefined
);
const zipDownloadHref = computed(() =>
  projectId.value ? `/api/v1/projects/${projectId.value}/action_reports_zip/` : undefined
);
const docxDownloadHrefGroup = (actionGroupId) =>
  `/api/v1/actiongroups/${actionGroupId}/action_group_report/`;

/* ---------------------------------------
 * Energies: union (Vue 2 rules kept)
 * EE: <= 7; NE: 7..12; RE: 21..30; EN: >= 31
 * ------------------------------------- */
const energies = computed(() => {
  const totalKeys = Object.keys(totalResult.value?.energySavings || {});
  const actionKeys = (actionResults.value || [])
    .map((res) => (res.energySavings ? Object.keys(res.energySavings) : []))
    .flat();

  const all = new Set([...totalKeys, ...actionKeys].map((k) => parseInt(k, 10)));
  const filtered = Array.from(all).filter(
    (x) => Number.isInteger(x) && (x <= 12 || x >= 21)
  );
  filtered.sort((a, b) => a - b);
  return filtered;
});

const energiesEElength = computed(() => energies.value.filter((n) => n <= 7).length);
const energiesNElength = computed(() => energies.value.filter((n) => n >= 7 && n <= 12).length);
const energiesRElength = computed(() => energies.value.filter((n) => n >= 21 && n <= 30).length);
const energiesENlength = computed(() => energies.value.filter((n) => n >= 31).length);

/* ---------------------------------------
 * Action list and selection for calc
 * ------------------------------------- */
const actionList = computed(() => store.currentProjectActions || []);



// const buildActionCalcList = () => {
//   const allIds = (actionList.value || []).map((a) => a.id);
//   const excluded = new Set(store.noCalc || []);
//   return allIds.filter((id) => !excluded.has(id));
// };

// id -> generator_type[] (same as Vue 2)
const buildConsMitGens = () => {
  const result = {};
  const ids = new Set(store.forCalc); // already unique, so Set is perfect

  for (const item of actionList.value || []) {
    if (!ids.has(item.id)) continue;

    if (Array.isArray(item.generator_type) && item.generator_type.length > 0) {
      result[item.id] = item.generator_type;
    }
  }

  // assign only the values (flattened)
  allGens.value = Object.values(result).flat();

  return result;
};

/* Legend colors (Option A: global color system via store.genColors) */
const genColors = computed(() => store.genColors || {}); // expects mapping from store

const coldGenList = computed(() => {
  const gensSet = new Set(Object.values(buildConsMitGens()).flat());
  const result = new Set();

  for (const a of actionList.value) {
    if (!gensSet.has(a.id)) continue;
    if (!a || !a.method) continue;
    if (a.method === 'chiller') {
      result.add(Number(a.id));
      continue;
    }
    if (a.method === 'freegen' && a.planned?.main_generated_energy === 12) {
      result.add(Number(a.id));
    }
  }
  return Array.from(result);
});

const warmGenList = computed(() => {
  const gensSet = new Set(Object.values(buildConsMitGens()).flat());
  const result = new Set();

  for (const a of actionList.value) {
    if (!gensSet.has(a.id)) continue;
    if (!a || !a.method) continue;

    // Exclude chillers from warm list
    if (a.method === 'chiller') continue;

    // For freegen, only include if it generates heat (main_generated_energy === 11)
    if (a.method === 'freegen') {
      if (a.planned?.main_generated_energy === 11) {
        result.add(Number(a.id));
      }
      continue;
    }

    // Include all other generator methods (boiler, etc.)
    result.add(Number(a.id));
  }
  return Array.from(result);
});


const createShowWarmColors = computed(() => {
  const gens = warmGenList.value;
  const gensToShow = gens.filter((gen) => Array.from(store.forCalc).includes(gen));
  return Object.fromEntries(
    Object.entries(genColors.value).filter(([key]) => gensToShow.includes(Number(key)))
  );
});

const createShowColdColors = computed(() => {
  const gens = coldGenList.value;
  const gensToShow = gens.filter((gen) => Array.from(store.forCalc).includes(gen));
  return Object.fromEntries(
    Object.entries(genColors.value).filter(([key]) => gensToShow.includes(Number(key)))
  );
});

/* ---------------------------------------
 * API calls
 * ------------------------------------- */
async function fetchMethodTexts() {
  try {
    const { data } = await api.get("/method_text/");
    methodTexts.value = data;
  } catch (err) {
    console.error("Failed to load method texts", err);
  }
}

async function fetchEnergyNames() {
  try {
    const { data } = await api.get("/projects/fuels/");
    const map = {};
    for (const energy of data || []) {
      map[energy.id] = energy.name;
    }
    fuels.value = map;
  } catch (err) {
    console.error("Failed to load fuels list", err);
  }
}

async function calcActions() {
  try {
    // const actionCalcList = buildActionCalcList();
    // console.log("Calculating actions with IDs:", actionCalcList);
    const consMitGens = buildConsMitGens();

    const { data } = await api.post(`/projects/${projectId.value}/calc/`, {
      actions: Array.from(store.forCalc),
      ConsMitGen_ids: consMitGens,
    });

    calcResponse.value = data;
    warningResponse.value = data?.result?.warnings || [];
    errorResponse.value = null;

    // Legend colors:
    showWarmColors.value = createShowWarmColors.value;
    showColdColors.value = createShowColdColors.value;
  } catch (err) {
    const status = err?.response?.status;
    if (status && status < 500) {
      errorResponse.value = err?.response?.data || ["Unbekannter Fehler"];
    }
    toast.error("Die Berechnung konnte nicht durchgeführt werden.");
    console.error("Calculation failed", err);
  }
}

/* ---------------------------------------
 * UI interactions
 * ------------------------------------- */
function toggleGroup(id) {
  const idx = toggledActionGroup.value.indexOf(id);
  if (idx > -1) toggledActionGroup.value.splice(idx, 1);
  else toggledActionGroup.value.push(id);
}

function handleToggleAction(id) {
  const idx = toggledAction.value.indexOf(id);
  if (idx > -1) toggledAction.value.splice(idx, 1);
  else toggledAction.value.push(id);
}

function togglePercentageView() {
  showPercentages.value = !showPercentages.value;
}

/* ---------------------------------------
 * Other derived
 * ------------------------------------- */
const showFeedInColumn = computed(() => {
  // Vue 2 logic: show if not zero (exists)
  const val = totalResult.value?.feedInProfits;
  return typeof val === "number" ? val !== 0 : false;
});

function getGenerator(id) {
  const action = (actionList.value || []).find((a) => a.id === id);
  return action ? action.name : "Generator n/a";
}

/* ---------------------------------------
 * Lifecycle
 * ------------------------------------- */
onBeforeMount(async () => {
  // Clear previous calc data
  calcResponse.value = null;
  errorResponse.value = null;
  warningResponse.value = null;
  
}); 

onMounted(async () => {
  // Load project (Pinia)
  if (projectId.value) {
    await store.fetchCurrentProject(projectId.value);
  }

  // Page help content (same approach as ProjectList.vue)
  store.setHelpComponent(ProjectCalcHelp);

  // Static/load-once data
  await Promise.all([fetchEnergyNames(), fetchMethodTexts()]);
});

// Recalc when actions list changes (and on first load)
watch(
  () => store.currentProjectActions,
  async (list) => {
    if (list && list.length) {
      await calcActions();
    }
  },
  { immediate: true }
);

// Keep title in sync with current project
watch(
  () => store.currentProject,
  (project) => {
    if (project?.name) {
      store.setTitle(project.name);
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.project-card {
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
}

/* ========================================
   Toolbar
======================================== */

.project-toolbar {
  min-height: auto;
  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.project-toolbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  width: 100%;
  padding: 1.25rem 1.5rem;
}

.project-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 260px;
}

.project-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

.project-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.toolbar-button {
  font-weight: 600;
  text-transform: none;
}

/* ========================================
   Calculation messages
======================================== */

.calculation-message {
  border-radius: 20px;
}

.message-description {
  margin-bottom: 0.65rem;
}

.message-list {
  display: grid;
  gap: 0.5rem;

  margin: 0;
  padding-left: 1.25rem;
}

.message-list li {
  line-height: 1.45;
}

/* ========================================
   Summary
======================================== */

.analysis-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 0.9rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 22px;

  background-color: rgba(var(--v-theme-primary), 0.035);
}

.analysis-summary-left {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.analysis-summary-right {
  text-align: right;
}

/* ========================================
   Table container
======================================== */

.analysis-table-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 22px;

  background-color: rgb(var(--v-theme-surface));
}

.project-table {
  width: 100%;
  background-color: transparent;
}

/*
 * Vuetify's internal wrapper controls horizontal
 * and vertical table scrolling.
 */
.project-table :deep(.v-table__wrapper) {
  position: relative;
  max-height: 68vh;
  overflow: auto;
}

.project-table :deep(table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

/* ========================================
   Sticky vertical header
======================================== */

.project-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 40;
}

.project-table :deep(thead th) {
  color: rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-surface));

  font-weight: 700;
  line-height: 1.25;
  white-space: normal;

  box-shadow: 0 1px 0 rgba(var(--v-theme-on-surface), 0.1);
}

/*
 * Keep both header rows opaque while the body
 * scrolls vertically below them.
 */
.project-table :deep(thead tr:first-child th) {
  background-color: rgb(var(--v-theme-surface));
}

.project-table :deep(thead tr:nth-child(2) th) {
  background-color: rgb(var(--v-theme-surface));
}

/* Multi-column header groups */
.project-table :deep(thead .alt) {
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.22);
  background-color: rgba(var(--v-theme-primary), 0.045);
}

/*
 * The Kurzbericht header is part of the vertically
 * sticky header, but it is NOT horizontally sticky.
 */
.project-table :deep(thead .sticky-report-column) {
  position: static !important;
  top: auto !important;
  right: auto !important;
  bottom: auto !important;
  z-index: auto !important;

  width: 96px;
  min-width: 96px;
  max-width: 96px;

  background-color: rgb(var(--v-theme-surface));
}

/* ========================================
   Table body
======================================== */

.project-table :deep(tbody td) {
  white-space: nowrap;
}

.project-table :deep(tbody tr) {
  transition: background-color 160ms ease;
}

.project-table :deep(tbody tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.055);
}

/* ========================================
   Group rows
======================================== */

.project-table :deep(tbody tr .group),
.project-table :deep(tbody tr .grouptitle) {
  cursor: pointer;
  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(var(--v-theme-primary), 0.065) !important;
}

.project-table :deep(tbody tr:hover .group),
.project-table :deep(tbody tr:hover .grouptitle) {
  background-color: rgba(var(--v-theme-primary), 0.105) !important;
}

.project-table :deep(.grouptitle) {
  font-weight: 700;
}

.group-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ========================================
   Shared Kurzbericht column dimensions
======================================== */

.project-table :deep(.sticky-report-column) {
  width: 96px;
  min-width: 96px;
  max-width: 96px;
}

/* ========================================
   Horizontally sticky body report cells

   These cells move normally up and down.
======================================== */

.project-table :deep(tbody .sticky-report-column) {
  position: sticky;
  right: 0;

  top: auto;
  bottom: auto;

  /*
   * Body controls remain below the sticky
   * header and sticky totals footer.
   */
  z-index: 10;

  padding-inline: 6px;
  background-color: rgb(var(--v-theme-surface));
  text-align: center;
}

/* Match ordinary row hover color */
.project-table :deep(tbody tr:hover .sticky-report-column) {
  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.055),
      rgba(var(--v-theme-primary), 0.055)
    ),
    rgb(var(--v-theme-surface));
}

/* Preserve group styling in the sticky report cell */
.project-table :deep(tbody .group.sticky-report-column) {
  
  position: sticky !important;
  right: 0 !important;

  /* Move normally during vertical scrolling */
  top: auto !important;
  bottom: auto !important;

  /* Below sticky header and footer */
  z-index: 10 !important;

  width: 96px;
  min-width: 96px;
  max-width: 96px;

  padding-inline: 6px;
  text-align: center;
  vertical-align: middle;

  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.065),
      rgba(var(--v-theme-primary), 0.065)
    ),
    rgb(var(--v-theme-surface)) !important;
}

.project-table :deep(tbody tr:hover .group.sticky-report-column) {
  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.105),
      rgba(var(--v-theme-primary), 0.105)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* ========================================
   Sticky totals footer

   Requires:
   <tfoot>
     <tr class="total-row">
======================================== */

.project-table :deep(tfoot) {
  position: sticky;
  bottom: 0;
  z-index: 30;
}

.project-table :deep(tfoot td) {
  color: rgb(var(--v-theme-on-surface));

  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.07)
    ),
    rgb(var(--v-theme-surface));

  font-weight: 700;
  white-space: nowrap;

  border-top: 2px solid rgba(var(--v-theme-primary), 0.22);
}

/*
 * Footer report cell is sticky both at the
 * bottom and on the right.
 */
.project-table :deep(tfoot .sticky-report-column) {
  position: sticky;
  right: 0;
  bottom: 0;
  z-index: 35;

  width: 96px;
  min-width: 96px;
  max-width: 96px;

  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.07)
    ),
    rgb(var(--v-theme-surface));
}

.total-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ========================================
   Legends
======================================== */

.analysis-bottom-bar {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
}

.legend-card {
  min-width: 260px;
  height: 100%;
  padding: 1rem;
  border-radius: 20px;
}

.legend-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  margin-bottom: 0.9rem;

  font-size: 0.95rem;
  font-weight: 700;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}

.legend-item:last-child {
  margin-bottom: 0;
}

.generator-color-dot {
  display: inline-block;
  flex-shrink: 0;

  width: 11px;
  height: 11px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-on-surface), 0.35);
  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}

/* ========================================
   Export area
======================================== */

.export-card {
  height: 100%;
  border-radius: 22px;
}

.export-card-content {
  display: flex;
  align-items: center;
  gap: 1.25rem;

  height: 100%;
  padding: 1rem;
}

.export-title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.export-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.export-actions .v-btn {
  font-weight: 600;
  text-transform: none;
}

/* ========================================
   Medium screens
======================================== */

@media (max-width: 1100px) {
  .project-toolbar-content {
    align-items: stretch;
    flex-direction: column;
  }

  .project-toolbar-actions {
    justify-content: flex-start;
  }

  .analysis-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .analysis-summary-right {
    text-align: left;
  }

  .analysis-bottom-bar {
    flex-wrap: wrap;
  }

  .export-card-content {
    align-items: flex-start;
    flex-direction: column;
  }
}

/* ========================================
   Small screens
======================================== */

@media (max-width: 700px) {
  .project-toolbar-content {
    padding: 1rem;
  }

  .project-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .project-toolbar-actions .v-btn {
    width: 100%;
  }

  .analysis-table-wrapper {
    border-radius: 18px;
  }

  .project-table :deep(.v-table__wrapper) {
    max-height: 60vh;
  }

  .project-table :deep(.sticky-report-column),
  .project-table :deep(thead .sticky-report-column),
  .project-table :deep(tfoot .sticky-report-column) {
    width: 88px;
    min-width: 88px;
    max-width: 88px;
  }

  .project-table :deep(tbody td.group.sticky-report-column) {
    width: 88px;
    min-width: 88px;
    max-width: 88px;
  }

  .legend-card {
    min-width: 100%;
  }

  .export-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .export-actions .v-btn {
    width: 100%;
  }
}
</style>